/**
 * Platform Dashboard
 * 
 * Super admin overview showing:
 * - Platform-wide statistics
 * - Recent activity
 * - System health
 * - Quick actions
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Users,
  FileText,
  RefreshCcw,
  TrendingUp,
  TrendingDown,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Plus,
  ArrowRight,
  Shield,
  Sparkles,
} from 'lucide-react';
import { getPlatformStats, listAllWorkspaces, type PlatformStats, type WorkspaceOverview } from '../../lib/superAdminService';

// ============================================================================
// Stat Card Component
// ============================================================================

interface StatCardProps {
  title: string;
  value: number | string;
  icon: typeof Building2;
  trend?: { value: number; direction: 'up' | 'down' };
  color: 'blue' | 'green' | 'purple' | 'amber' | 'red';
  href?: string;
}

function StatCard({ title, value, icon: Icon, trend, color, href }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    green: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    purple: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    amber: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    red: 'bg-[var(--coral-dim)] text-[var(--coral)]',
  };
  
  const content = (
    <div className={`bg-[var(--bg-raised)]  border border-[var(--rule)] p-6 
                     ${href ? 'hover:border-[var(--rule)] hover:shadow-md transition-all cursor-pointer' : ''}`}>
      <div className="flex items-start justify-between">
        <div className={`p-3  ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-sm font-medium
                          ${trend.direction === 'up' ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'}`}>
            {trend.direction === 'up' ? (
              <TrendingUp className="h-4 w-4" />
            ) : (
              <TrendingDown className="h-4 w-4" />
            )}
            {trend.value}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="text-3xl font-bold text-[var(--text-pri)]">{value}</p>
        <p className="text-sm text-[var(--text-ter)] mt-1">{title}</p>
      </div>
    </div>
  );
  
  if (href) {
    return <Link to={href}>{content}</Link>;
  }
  
  return content;
}

// ============================================================================
// Health Indicator
// ============================================================================

interface HealthItem {
  name: string;
  status: 'healthy' | 'warning' | 'error';
  message: string;
  lastCheck: Date;
}

function HealthIndicator({ item }: { item: HealthItem }) {
  const statusConfig = {
    healthy: { icon: CheckCircle, color: 'text-[var(--green)]', bg: 'bg-[var(--green-dim)]' },
    warning: { icon: AlertTriangle, color: 'text-[var(--mustard)]', bg: 'bg-[var(--mustard-dim)]' },
    error: { icon: AlertTriangle, color: 'text-[var(--coral)]', bg: 'bg-[var(--coral-dim)]' },
  };
  
  const config = statusConfig[item.status];
  const StatusIcon = config.icon;
  
  return (
    <div className={`flex items-center justify-between p-4  ${config.bg}`}>
      <div className="flex items-center gap-3">
        <StatusIcon className={`h-5 w-5 ${config.color}`} />
        <div>
          <p className="font-medium text-[var(--text-pri)]">{item.name}</p>
          <p className="text-sm text-[var(--text-ter)]">{item.message}</p>
        </div>
      </div>
      <div className="text-xs text-[var(--text-ter)]">
        <Clock className="inline h-3 w-3 mr-1" />
        {item.lastCheck.toLocaleTimeString()}
      </div>
    </div>
  );
}

// ============================================================================
// Recent Workspaces Table
// ============================================================================

function RecentWorkspacesTable({ workspaces }: { workspaces: WorkspaceOverview[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider">
            <th className="px-4 py-3">Workspace</th>
            <th className="px-4 py-3">Owner</th>
            <th className="px-4 py-3">Plan</th>
            <th className="px-4 py-3">Members</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--rule)]">
          {workspaces.map(ws => (
            <tr key={ws.id} className="hover:bg-[var(--surface)]">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[var(--hover-tint)]">
                    <Building2 className="h-4 w-4 text-[var(--text-sec)]" />
                  </div>
                  <div>
                    <p className="font-medium text-[var(--text-pri)]">{ws.name}</p>
                    <p className="text-xs text-[var(--text-ter)]">{ws.slug}</p>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-[var(--text-sec)]">
                {ws.ownerEmail || '—'}
              </td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 text-xs font-medium rounded-full
                  ${ws.plan === 'enterprise' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' :
                    ws.plan === 'professional' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' :
                    ws.plan === 'starter' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
                    'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
                  {ws.plan}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-[var(--text-sec)]">
                {ws.memberCount}
              </td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 text-xs font-medium rounded-full
                  ${ws.status === 'active' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
                    ws.status === 'demo' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' :
                    ws.status === 'suspended' ? 'bg-[var(--coral-dim)] text-[var(--coral)]' :
                    'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
                  {ws.status}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-[var(--text-ter)]">
                {new Date(ws.createdAt).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Main Dashboard
// ============================================================================

export default function PlatformDashboard() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [recentWorkspaces, setRecentWorkspaces] = useState<WorkspaceOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsData, workspacesData] = await Promise.all([
        getPlatformStats(),
        listAllWorkspaces(),
      ]);
      setStats(statsData);
      setRecentWorkspaces(workspacesData.slice(0, 10));
    } catch (err) {
      console.error('Error loading platform data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load platform data');
      setStats(null);
      setRecentWorkspaces([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // System health (mock for now)
  const healthItems: HealthItem[] = [
    { name: 'Database', status: 'healthy', message: 'All queries responding normally', lastCheck: new Date() },
    { name: 'Authentication', status: 'healthy', message: 'Supabase Auth active', lastCheck: new Date() },
    { name: 'Background Jobs', status: 'healthy', message: 'No pending jobs', lastCheck: new Date() },
    { name: 'Storage', status: 'warning', message: '78% capacity used', lastCheck: new Date() },
  ];
  
  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-[var(--hover-tint)] rounded w-48" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-[var(--hover-tint)]" />
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-8 space-y-8">
      {error && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4 flex items-center justify-between">
          <span className="text-[var(--coral)]">{error}</span>
          <button
            type="button"
            onClick={loadData}
            className="shrink-0 px-3 py-1.5 bg-[var(--coral-dim)] text-[var(--coral)] hover:bg-[var(--coral-dim)]"
          >
            Retry
          </button>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-pri)]">Platform Dashboard</h1>
          <p className="text-[var(--text-ter)] mt-1">Overview of your Peak Priorities platform</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors disabled:opacity-50"
          >
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
          <Link
            to="/platform-admin/tenants/new"
            className="flex items-center gap-2 px-4 py-2 bg-[var(--coral)] text-white hover:bg-[var(--coral)] transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Tenant
          </Link>
        </div>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Workspaces"
          value={stats?.totalWorkspaces ?? 0}
          icon={Building2}
          color="blue"
          href="/platform-admin/workspaces"
        />
        <StatCard
          title="Total Users"
          value={stats?.totalUsers ?? 0}
          icon={Users}
          color="green"
        />
        <StatCard
          title="Total Items"
          value={stats?.totalCandidates || 0}
          icon={FileText}
          color="purple"
        />
        <StatCard
          title="Active Processes"
          value={stats?.totalProcesses || 0}
          icon={RefreshCcw}
          color="amber"
        />
      </div>
      
      {/* Plans Breakdown */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Workspaces by Plan</h2>
        <div className="grid grid-cols-4 gap-4">
          {Object.entries(stats?.workspacesByPlan || {}).map(([plan, count]) => (
            <div key={plan} className="text-center p-4 bg-[var(--surface)]">
              <p className="text-2xl font-bold text-[var(--text-pri)]">{count}</p>
              <p className="text-sm text-[var(--text-ter)] capitalize">{plan}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* System Health */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">System Health</h2>
            <Activity className="h-5 w-5 text-[var(--text-ter)]" />
          </div>
          <div className="space-y-3">
            {healthItems.map(item => (
              <HealthIndicator key={item.name} item={item} />
            ))}
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="lg:col-span-2 bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">Quick Actions</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to="/platform-admin/tenants/new"
              className="flex items-center gap-4 p-4 border border-[var(--rule)] hover:border-[var(--coral)] hover:bg-[var(--coral-dim)] transition-all group"
            >
              <div className="p-3 bg-[var(--coral-dim)] group-hover:bg-[var(--coral-dim)] transition-colors">
                <Building2 className="h-5 w-5 text-[var(--coral)]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[var(--text-pri)]">Create Tenant</p>
                <p className="text-sm text-[var(--text-ter)]">New organization + default workspace</p>
              </div>
              <ArrowRight className="h-5 w-5 text-[var(--text-ter)] group-hover:text-[var(--coral)] transition-colors" />
            </Link>
            
            <Link
              to="/platform-admin/workspaces?filter=demo"
              className="flex items-center gap-4 p-4 border border-[var(--rule)] hover:border-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-all group"
            >
              <div className="p-3 bg-[var(--mustard-dim)] group-hover:bg-[var(--mustard-dim)] transition-colors">
                <Sparkles className="h-5 w-5 text-[var(--mustard)]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[var(--text-pri)]">Demo Workspaces</p>
                <p className="text-sm text-[var(--text-ter)]">{stats?.demoWorkspaces || 0} active demos</p>
              </div>
              <ArrowRight className="h-5 w-5 text-[var(--text-ter)] group-hover:text-[var(--mustard)] transition-colors" />
            </Link>
            
            <Link
              to="/platform-admin/admin-roles"
              className="flex items-center gap-4 p-4 border border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-all group"
            >
              <div className="p-3 bg-[var(--slate-dim)] group-hover:bg-[var(--slate-dim)] transition-colors">
                <Shield className="h-5 w-5 text-[var(--text-ter)]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[var(--text-pri)]">Admin Roles</p>
                <p className="text-sm text-[var(--text-ter)]">Platform, tenant & workspace admins</p>
              </div>
              <ArrowRight className="h-5 w-5 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] transition-colors" />
            </Link>
            
          </div>
        </div>
      </div>
      
      {/* Recent Workspaces */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-[var(--rule)]">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">Recent Workspaces</h2>
          <Link
            to="/platform-admin/workspaces"
            className="text-sm text-[var(--coral)] hover:text-[var(--coral)] font-medium"
          >
            View all →
          </Link>
        </div>
        <RecentWorkspacesTable workspaces={recentWorkspaces} />
      </div>
    </div>
  );
}
